// Libraries
import { ValidationError } from "class-validator";
import { action, observable } from "mobx";

// Core
import { StoresInstance } from "Core/Base";
import { ModelBase } from "Core/Models/ModelBase";

// Custom
import { AccountStore } from "Custom/Stores/Domain";
import { isNullOrUndefined } from "Custom/Utils/Utils";

import { GutterSystemModel } from "./SubModels/GutterSystem";
import { ProductsModel } from "./SubModels/Products";
import { QuoteModel } from "./SubModels/Quote";
import { RoofSystemModel } from "./SubModels/RoofSystem";
import { DelcoteGSModelDTO } from "./SubModels/GutterSystemSubModels/DelcoteProductModels/DelcoteGS";
import { PlygeneMouldedModelDTO } from "./SubModels/GutterSystemSubModels/PlygeneProductModels/PlygeneMoulded";
import { PlygenStandardModelDTO } from "./SubModels/GutterSystemSubModels/PlygeneProductModels/PlygeneStandard";
import { Delcote10ModelDTO } from "./SubModels/RoofSystemSubModels/DelcoteProductModels/Delcote10";
import { Delcote25ModelDTO } from "./SubModels/RoofSystemSubModels/DelcoteProductModels/Delcote25";
import { DelcoteAsbestosModelDTO } from "./SubModels/RoofSystemSubModels/DelcoteProductModels/DelcoteAsbestos";
import { DelglazeModelDTO } from "./SubModels/RoofSystemSubModels/Delglaze";
import { Seamsil100ModelDTO } from "./SubModels/RoofSystemSubModels/SeamsilProductModels/Seamsil100";

export class ProductSelectorModel extends ModelBase<ProductSelectorModel, ProductsModelDTO> {
    private accountStore: AccountStore = StoresInstance.domain.AccountStore;
    private gutterSystemModel: GutterSystemModel;
    private productsModel: ProductsModel;
    private quoteModel: QuoteModel;
    private roofSystemModel: RoofSystemModel;

    @observable haveSubmitted: boolean = true;

    constructor() {
        super();

        this.gutterSystemModel = new GutterSystemModel();
        this.productsModel = new ProductsModel();
        this.quoteModel = new QuoteModel();
        this.roofSystemModel = new RoofSystemModel();
    }

    // #region Model Properties

    public get GutterSystem(): GutterSystemModel {
        return this.gutterSystemModel;
    }

    public get Products(): ProductsModel {
        return this.productsModel;
    }

    public get Quote(): QuoteModel {
        return this.quoteModel;
    }

    public get RoofSystem(): RoofSystemModel {
        return this.roofSystemModel;
    }

    // #endregion Model Properties

    public fromDto(model: ProductsModelDTO): void {
        throw new Error("Method not implemented.");
    }

    public toDto(): ProductsModelDTO {
        const gutterSystem =
            this.Products.productSelection.value === "id_guttersystem_only" || this.Products.productSelection.value === "id_roofandgutter" ? this.GutterSystem.toDto() : undefined;

        const roofSystem =
            this.Products.productSelection.value === "id_roofsystem_only" || this.Products.productSelection.value === "id_roofandgutter" ? this.RoofSystem.toDto() : undefined;

        return {
            quoteRequestGutter: !isNullOrUndefined(gutterSystem)
                ? {
                      delcoteGSGutterCoating: gutterSystem?.delcoteProducts?.delcoteGS,
                      plygeneMouldedGutterLiningSystem: gutterSystem?.plygeneProducts?.plygeneMoulded,
                      plygeneStandardGutterLiningSystem: gutterSystem?.plygeneProducts?.plygeneStandard,
                  }
                : undefined,
            quoteRequestRoof: !isNullOrUndefined(roofSystem)
                ? {
                      delcote10MetalRoofCoating: roofSystem?.delcoteProducts?.delcote10,
                      delcote25MetalRoofCoating: roofSystem?.delcoteProducts?.delcote25,
                      delcoteAsbestosRoofCoating: roofSystem?.delcoteProducts?.delcoteAsbestos,
                      delglazeRooflightTreatment: roofSystem?.delglaze,
                      seamsil100: roofSystem?.seamsilProducts?.seamsil100,
                  }
                : undefined,
        };
    }

    @action
    private setHaveSubmitted = (value: boolean) => {
        this.haveSubmitted = value;
    };

    public Validate = async (): Promise<ValidationError[]> => {
        this.setHaveSubmitted(true);
        this.accountStore.setSubmitted(true);

        const gutterSystemSelected = this.Products.productSelection.value === "id_guttersystem_only" || this.Products.productSelection.value === "id_roofandgutter";
        const roofSystemSelected = this.Products.productSelection.value === "id_roofsystem_only" || this.Products.productSelection.value === "id_roofandgutter";
        const validationErrors: ValidationError[] = [];

        if (gutterSystemSelected) {
            const errors = await this.gutterSystemModel.Validate();

            if (errors.length > 0) {
                validationErrors.push(...errors);
            }
        }

        if (roofSystemSelected) {
            const errors = await this.roofSystemModel.Validate();

            if (errors.length > 0) {
                validationErrors.push(...errors);
            }
        }

        return validationErrors;
    };
}

export interface ProductsModelDTO {
    quoteRequestGutter?: QuoteRequestGutter;
    quoteRequestRoof?: QuoteRequestRoof;
}

interface QuoteRequestGutter {
    delcoteGSGutterCoating?: DelcoteGSModelDTO;
    plygeneMouldedGutterLiningSystem?: PlygeneMouldedModelDTO;
    plygeneStandardGutterLiningSystem?: PlygenStandardModelDTO;
}

interface QuoteRequestRoof {
    delcote10MetalRoofCoating?: Delcote10ModelDTO;
    delcote25MetalRoofCoating?: Delcote25ModelDTO;
    delcoteAsbestosRoofCoating?: DelcoteAsbestosModelDTO;
    delglazeRooflightTreatment?: DelglazeModelDTO;
    seamsil100?: Seamsil100ModelDTO;
}
